import { AdapterInterface } from "./Adapter.interface";

/**
 * The two spellings `Key.floor_indexing` may hold on the wire.
 *
 * This field is NOT the `VendorFloorIndexing` enum that its neighbour
 * `Adapter.vendor_floor_indexing` uses. `archive/key.fbs` declares it
 * `floor_indexing: string`, LiftCloud binds it as
 * `oneof=zero_based one_based` into a `varchar(10)`, and VTS's CDO and LN1
 * builders branch on these exact two strings. An integer sent here is a 400
 * from the API and, if it got past that, an unrecognised indexing in VTS.
 *
 * Kept as named constants so the tables that render them and the normaliser
 * that produces them cannot drift apart on a respelling.
 */
export const KEY_FLOOR_INDEXING = {
  ONE_BASED: "one_based",
  ZERO_BASED: "zero_based",
} as const;

/**
 * A key's floor-indexing override. `null` means inherit from the adapter's
 * `vendor_floor_indexing` — the column is nullable for exactly that reason.
 */
export type KeyFloorIndexing = (typeof KEY_FLOOR_INDEXING)[keyof typeof KEY_FLOOR_INDEXING] | null;

export interface KeyInterface {
  [key: string]: any; // Index signature
  id?: string;
  adapterId: string;
  adapter?: AdapterInterface;
  encodedType: number;
  messageContent: number;
  messageValue: number;
  fbsDeviceType: string;
  position?: number | null;
  value: string;
  identifier: any;
}

export interface KeyTableActionsInterface {
  selectedItem: KeyInterface;
}

export interface KeyImportInterface {
  adapter_id: string;
  csv: File | undefined;
}

export interface DrawerKeyInformationInterface {
  data: KeyInterface;
}

export interface KeysAdapterKeyTableInterface {
  [key: string]: any; // Index signature
  isValid: boolean;
  duplicatedValue: "warning" | "error" | "";
  idKey: string;
  methodType: string;
  idMessage: string; // UI row identifier only — not sent to API
  message: string;
  type: string;
  idType: number; // = message_content (1–9)
  encoded_type: number;
  message_value: number;
  fbs_device_type: string;
  contentType: number;
  lower_encoded_type: number | null;
  lower_message_content: number | null;
  lower_message_value: number | null;
  lower_fbs_device_type: string | null;
  lower_message_label: string | null;
  data: boolean;
  bit_offset: number;
  byte_offset: number;
  units_based: boolean;
  floors_based: boolean;
  floor_indexing: KeyFloorIndexing;
  debounce_time: number | null;
}

export interface KeysAdapterOffsetsTableInterface {
  [key: string]: any;
  idMessageType: number;
  isValid: boolean;
  type: string;
  id: string;
  subType: string;
}

export interface KeysAdapterOffsetsInteface {
  message_sub_type: string;
  message_type: number;
  offset: number;
  unit_number: number;
}
